// import { ContextManager, registerLoadable } from '@zcomponent/core';


// import { Object3D } from '@zcomponent/three/lib/components/Object3D';
import { fetchProfile, getAssetURL } from './controllerprofiles';
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { clone } from 'three/examples/jsm/utils/SkeletonUtils';
import { Jute } from '../../../..';
import { Profile } from './profile';
import { Mesh, Object3D } from 'three';

type IOnResetButtonAvailableCb = (m: Mesh, controller: XRControllerModel) => unknown;

interface XRControllerModelConstructorProps {
	/**
	 * An array of the XR input profile names to attempt to load. These profile names are defined in the
	 * `@webxr-input-profiles/assets` package where manufacturers document the controllers for their device.
	 * If the array contains multiple items, a model is requested from the first element to last until one is matched
	 * from the package. This allows devices to report multiple profiles for fallback purposes.
	 */
	profiles: string[];

	/**
	 * Specify a handedness (e.g. left handed or right handed) for controllers that have different representations for
	 * each hand. Some devices have only one controller, or have multiple but without a distinction between hands, and `none`
	 * can be used to indicate this. If `any` is passed, the first model listed in the `@webxr-input-profiles/assets`
	 * package for a given profile will be displayed.
	 */
	handedness: XRHandedness;
	resetButtonIndex?: number;
}

const _loader = new GLTFLoader();
const _gltfCache = new Map<string, Promise<GLTF>>();

function fetchGLB(url: string): Promise<GLTF> {
	let promise = _gltfCache.get(url);
	if (!promise) {
		promise = _loader.loadAsync(url);
		_gltfCache.set(url, promise);
	}
	return promise;
}

/**
 * A component that downloads and displays the 3D model for an XR controller based on a 'profile' (i.e. the make/model of the controller)
 * and handedness (i.e left or right handed). The models are acquired from the `@webxr-input-profiles/assets` package where
 * manufacturers document the controllers for their device.
 *
 * Note that it make take a few moments for a model to be resolved and downloaded. A promise is provided, `loaded` that will resolve
 * when the model is fully ready.
 */

export class XRControllerModel extends Object3D {
	private _loaded?: () => void;
	private _onResetButtonAvailableCbs: IOnResetButtonAvailableCb[] = []
	/**
	 * A promise that's resolved when the model is fully ready to display.
	 */
	public loaded = new Promise<void>(resolve => (this._loaded = resolve));

	/**
	 * Models representing hands (for devices that support hand tracking) contain bones representing
	 * the positions and rotations of the fingers of the hand. Once loaded, this object contains a
	 * mapping of joint name to the three.js bone in the model. For more information on this mapping,
	 * see https://developer.mozilla.org/en-US/docs/Web/API/XRHand
	 */
	public bonesByJointName = new Map<string, Object3D>();

	constructor(public constructorProps: XRControllerModelConstructorProps) {
        super();

		this._load(constructorProps.handedness, constructorProps.resetButtonIndex);

	}

	private _getMeshNameByButtonIndex = (profile: Profile, handedness: XRHandedness, gamepadIndices: number): string | null => {
		const { components } = profile.layouts[handedness];
		const componentId = Object.keys(components).find(key => components[key].gamepadIndices.button === gamepadIndices);
		if (!componentId) return null;
		const responses = components[componentId].visualResponses;
		if (!responses) return null;
		const response = Object.values(responses)[0];
		return response.minNodeName;
	}

	private async _load(handedness: XRHandedness, resetButtonIndex?: number) {
		let profile = await fetchProfile(Jute.isZapbox ? ['meta-quest-touch-plus'] : this.constructorProps.profiles ?? [], 'generic-trigger');
		// if (profile.profileId === "pico-phoneix") {
		// 	profile = await fetchProfile(['pico-neo3'])
		// }
		
		let assetURL = getAssetURL(profile, this.constructorProps.handedness ?? 'any');
		// if (Jute.isZapbox) {
		// 	assetURL = this.constructorProps.handedness === 'right' ? 
		// 		'https://d1mfzu0xo6h6ih.cloudfront.net/designer/zb-controller-right-v1.glb' : 
		// 		'https://d1mfzu0xo6h6ih.cloudfront.net/designer/zb-controller-left-v1.glb';
		// }
		if (!assetURL) return;
		const glb = await fetchGLB(assetURL);

		//console.log('controller model', glb)
		const cloned = clone(glb.scene);
		this.add(cloned);

		if (typeof resetButtonIndex !== 'undefined') {
			const meshName = this._getMeshNameByButtonIndex(profile, handedness, resetButtonIndex);
			if (meshName !== null) {
				const resetButtonMesh = cloned.getObjectByName(meshName) as Mesh;
				if (resetButtonMesh) this._resetButtonMeshAvailable(resetButtonMesh, this);
			}
		}

		const mesh = cloned.getObjectByProperty('type', 'SkinnedMesh');
		if (mesh) {
			mesh.frustumCulled = false;
			mesh.castShadow = true;
			mesh.receiveShadow = true;
		}

		this.bonesByJointName = new Map();

		const joints = [
			'wrist',
			'thumb-metacarpal',
			'thumb-phalanx-proximal',
			'thumb-phalanx-distal',
			'thumb-tip',
			'index-finger-metacarpal',
			'index-finger-phalanx-proximal',
			'index-finger-phalanx-intermediate',
			'index-finger-phalanx-distal',
			'index-finger-tip',
			'middle-finger-metacarpal',
			'middle-finger-phalanx-proximal',
			'middle-finger-phalanx-intermediate',
			'middle-finger-phalanx-distal',
			'middle-finger-tip',
			'ring-finger-metacarpal',
			'ring-finger-phalanx-proximal',
			'ring-finger-phalanx-intermediate',
			'ring-finger-phalanx-distal',
			'ring-finger-tip',
			'pinky-finger-metacarpal',
			'pinky-finger-phalanx-proximal',
			'pinky-finger-phalanx-intermediate',
			'pinky-finger-phalanx-distal',
			'pinky-finger-tip',
		];

		for (const jointName of joints) {
			const bone = cloned.getObjectByName(jointName);

			if (bone !== undefined) {
				this.bonesByJointName.set(jointName, bone);
			}
		}

		this._loaded?.();
		delete this._loaded;
	}

	public onResetButtonMeshAvailable = (cb: IOnResetButtonAvailableCb) => {
		this._onResetButtonAvailableCbs.push(cb)
	}

	private _resetButtonMeshAvailable = (mesh: Mesh, self: XRControllerModel) => {
		this._onResetButtonAvailableCbs.forEach(cb => cb(mesh, self));
	}

	// dispose() {
	// 	return super.dispose();
	// }
}
